// components/ProductDecorList.jsx
import React, { useMemo, useState, useEffect } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";

// ——— localStorage helper ———
function useLocalStorage(key, initial) {
  const [state, setState] = useState(() => {
    try {
      const s = localStorage.getItem(key);
      return s ? JSON.parse(s) : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch {}
  }, [key, state]);
  return [state, setState];
}

// ——— util: konverzija AverageRGB u HEX ———
function toHex(input) {
  if (!input) return null;
  let c = String(input).trim();
  // #RGB / #RRGGBB
  let m = c.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (m) {
    let h = m[1];
    if (h.length === 3) h = h.split("").map(x => x + x).join("");
    return "#" + h.toUpperCase();
  }
  // rgb(...) ili "r,g,b"
  const parts = c.replace(/rgb\(|\)/gi, "").split(/[\s,;|]+/).filter(Boolean);
  if (parts.length === 3 && parts.every(p => /^\d+$/.test(p))) {
    const nums = parts.map(n => Math.max(0, Math.min(255, parseInt(n, 10))));
    return (
      "#" + nums.map(n => n.toString(16).padStart(2, "0")).join("").toUpperCase()
    );
  }
  return null;
}

// ——— dekor kolone ———
const ALL_COLUMNS = [
  { key: "thumb", label: "Image", enableResizing: false, size: 70, minSize: 70, maxSize: 100 },
  { key: "id", label: "ID", size: 120 },
  { key: "DecorCode", label: "Decor Code", size: 160 },
  { key: "ManufacturerName", label: "Manufacturer Name", size: 200 },
  { key: "ColorAndCharacter", label: "Color/Character", size: 200 },
  { key: "Structure", label: "Structure", size: 160 },
  { key: "AverageRGB", label: "Average color", size: 160 },
  { key: "Texture", label: "Texture", size: 110 },
  { key: "URL", label: "URL", size: 260 },
];

const DEFAULT_VISIBLE = [
  "thumb", "DecorCode", "ManufacturerName", "ColorAndCharacter", "Structure", "AverageRGB", "Texture"
];

export default function ProductDecorList({
  decorations,
  loading,
  globalFilter = "",
  onEdit,
  onDelete, 
  onOpenImage
}) {
  // ——— kolone (uklj. posebne ćelije za sliku i AverageRGB) ———
  const baseCols = useMemo(() => {
    const serverBase = "https://modularobjects.com/products";
    const imageCell = ({ row }) => {
  const id = row.original?.id;
  const src = id
    ? `https://modularobjects.com/products/api/products/decor/${encodeURIComponent(id)}/image?ts=${Date.now()}`
    : null;

  const [err, setErr] = React.useState(false);

  return (
    <button
      type="button"
      className="w-12 h-12 rounded overflow-hidden bg-neutral-100 flex items-center justify-center hover:ring-2 hover:ring-blue-400"
      onClick={() => src && !err && onOpenImage?.(src)}
      title="Click to preview"
    >
      {src && !err ? (
        <img
          src={src}
          alt=""
          className="w-full h-full object-cover"
          onError={() => setErr(true)}
        />
      ) : (
        <span className="text-[10px] text-gray-500 px-1">no img</span>
      )}
    </button>
  );
};


    const avgColorCell = ({ getValue }) => {
      const hex = toHex(getValue()) || "#999999";
      return (
        <div className="flex items-center gap-2">
          <span
            className="inline-block w-6 h-6 rounded border"
            style={{ backgroundColor: hex }}
            title={hex}
          />
          <span className="text-xs">{hex}</span>
        </div>
      );
    };

    return ALL_COLUMNS.map((c) => {
      if (c.key === "thumb") {
        return {
          id: "thumb",
          header: c.label,
          size: c.size, minSize: c.minSize, maxSize: c.maxSize,
          enableResizing: c.enableResizing === false ? false : true,
          cell: imageCell,
          enableSorting: false,
          enableColumnFilter: false,
        };
      }
      if (c.key === "AverageRGB") {
        return {
          accessorKey: "AverageRGB",
          header: c.label,
          size: c.size, minSize: 120, maxSize: 220,
          cell: avgColorCell,
          filterFn: "includesString",
        };
      }
      if (c.key === "Texture") {
        return {
          accessorKey: "Texture",
          header: c.label,
          size: c.size, minSize: 90, maxSize: 150,
          cell: ({ getValue }) => (Number(getValue()) ? "Yes" : "No"),
          filterFn: (row, colId, value) => {
            // value: '', '1', '0'
            if (value === "") return true;
            const v = Number(row.getValue(colId)) ? "1" : "0";
            return v === value;
          },
        };
      }
      // default
      return {
        accessorKey: c.key,
        header: c.label,
        size: c.size || 160,
        minSize: 90,
        maxSize: 420,
        cell: (info) => String(info.getValue() ?? ""),
        filterFn: "includesString",
      };
    });
  }, []);

  // ——— dodaj “Actions” kolonu ———
  const columns = useMemo(
    () => [
      ...baseCols,
      {
        id: "actions",
        header: "Actions",
        size: 120,
        minSize: 120,
        maxSize: 200,
        enableResizing: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <div className="flex gap-2">
            <button
              className="bg-blue-500 text-white px-2 py-1 rounded text-xs hover:bg-blue-600"
              onClick={() => onEdit?.(row.original)}
            >
              ✏️
            </button>
            <button
              className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600"
              onClick={() => onDelete?.(row.original.id)}
            >
              🗑️
            </button>
          </div>
        ),
      },
    ],
    [baseCols, onEdit, onDelete]
  );

  // ——— state za tabelu + persistance ———
  const [columnVisibility, setColumnVisibility] = useLocalStorage(
    "decor.v2.colVisibility",
    Object.fromEntries(
      ALL_COLUMNS.map((c) => [c.key, DEFAULT_VISIBLE.includes(c.key)])
    )
  );
  const [columnOrder, setColumnOrder] = useLocalStorage(
    "decor.v2.colOrder",
    [...DEFAULT_VISIBLE, ...ALL_COLUMNS.map(c => c.key).filter(k => !DEFAULT_VISIBLE.includes(k)), "actions"]
  );
  const [columnSizing, setColumnSizing] = useLocalStorage("decor.v2.colSizing", {});
  const [columnSizingInfo, setColumnSizingInfo] = useState({});
  const [sorting, setSorting] = useState([{ id: "DecorCode", desc: false }]);
  const [columnFilters, setColumnFilters] = useLocalStorage("decor.v2.colFilters", []);
  const [global, setGlobal] = useState(globalFilter ?? "");

  // sync global filter iz props-a
  useEffect(() => { setGlobal(globalFilter ?? ""); }, [globalFilter]);

  const table = useReactTable({
    data: decorations || [],
    columns,
    state: {
      sorting,
      globalFilter: global,
      columnFilters,
      columnVisibility,
      columnOrder,
      columnSizing,
      columnSizingInfo,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobal,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onColumnSizingInfoChange: setColumnSizingInfo,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    columnResizeMode: "onChange",
    getRowId: (row) => row.id ?? row.DecorCode ?? Math.random().toString(36).slice(2),
  });

  // panel za kolone
  const [openCols, setOpenCols] = useState(false);
  const ordered = columnOrder
    .map((k) => (k === "actions" ? { key: "actions", label: "Actions" } : ALL_COLUMNS.find((c) => c.key === k)))
    .filter(Boolean);

  const moveCol = (key, dir) => {
    setColumnOrder((prev) => {
      const next = [...prev];
      const i = next.indexOf(key);
      if (i === -1) return prev;
      const j = dir === "left" ? i - 1 : i + 1;
      if (j < 0 || j >= next.length) return prev;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  if (loading) return <div className="text-center py-4">Loading decorations...</div>;

  return (
    <div className="space-y-3">
      {/* toolbar: global filter + columns panel */}
      <div className="flex items-center gap-2 flex-wrap">
        <input
          value={global}
          onChange={(e) => table.setGlobalFilter(e.target.value)}
          placeholder="Search all columns…"
          className="px-3 py-2 border rounded w-full max-w-md dark:bg-neutral-800 dark:text-white"
        />
        <button
          onClick={() => setOpenCols((o) => !o)}
          className="px-3 py-2 rounded border hover:bg-gray-50 dark:hover:bg-neutral-800"
        >
          Columns
        </button>
        {openCols && (
          <div className="z-20 border rounded shadow bg-white dark:bg-neutral-900 p-3 max-h-96 overflow-auto">
            {ordered.map((c) => (
              <div key={c.key} className="flex items-center justify-between py-1 gap-3">
                <label className="flex items-center gap-2">
                  {c.key !== "actions" && (
                    <input
                      type="checkbox"
                      checked={!!columnVisibility[c.key]}
                      onChange={() =>
                        setColumnVisibility((v) => ({ ...v, [c.key]: !v[c.key] }))
                      }
                    />
                  )}
                  {c.label}
                </label>
                <div className="flex gap-1">
                  <button
                    className="px-2 py-0.5 border rounded text-xs"
                    onClick={() => moveCol(c.key, "left")}
                  >
                    ◀
                  </button>
                  <button
                    className="px-2 py-0.5 border rounded text-xs"
                    onClick={() => moveCol(c.key, "right")}
                  >
                    ▶
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* tabela sa fiksnim/resizable kolonama */}
      <div className="overflow-auto border rounded max-h-[70vh]">
        <table className="min-w-full border-collapse table-fixed">
          <thead className="bg-gray-100 dark:bg-neutral-800 sticky top-0 z-10">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => {
                  const canResize = header.column.getCanResize();
                  const isResizing = header.column.getIsResizing();
                  const size = header.column.getSize();
                  return (
                    <th
                      key={header.id}
                      className="px-3 py-2 border text-left whitespace-nowrap cursor-pointer select-none relative"
                      onClick={header.column.getToggleSortingHandler()}
                      style={{ width: size, maxWidth: size, minWidth: size }}
                      title="Click to sort. Drag right edge to resize."
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getIsSorted() === "asc"
                        ? " ↑"
                        : header.column.getIsSorted() === "desc"
                        ? " ↓"
                        : ""}

                      {canResize && (
                        <div
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          className={`absolute top-0 right-0 h-full w-1 cursor-col-resize select-none touch-none ${
                            isResizing ? "bg-blue-500" : "bg-transparent"
                          }`}
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}

            {/* red sa filterima po koloni */}
            <tr>
              {table.getHeaderGroups()[0].headers.map((header) => {
                const col = header.column;
                if (col.id === "actions" || col.id === "thumb") {
                  return <th key={col.id} className="px-2 py-1 border" />;
                }

                // Texture kao dropdown, ostalo text input
                if (col.id === "Texture") {
                  const v = (col.getFilterValue() ?? "") + "";
                  return (
                    <th key={col.id} className="px-2 py-1 border">
                      <select
                        className="w-full px-2 py-1 border rounded text-sm dark:bg-neutral-800 dark:text-white"
                        value={v}
                        onChange={(e) => col.setFilterValue(e.target.value)}
                      >
                        <option value="">All</option>
                        <option value="1">Yes</option>
                        <option value="0">No</option>
                      </select>
                    </th>
                  );
                }

                // default: text filter
                return (
  <th key={col.id} className="px-2 py-1 border">
    <input
      className="w-full px-2 py-1 border rounded text-sm dark:bg-neutral-800 dark:text-white"
      value={String(col.getFilterValue() ?? "")}
      onChange={(e) => col.setFilterValue(e.target.value)}
      placeholder="Filter…"
    />
  </th>
);

              })}
            </tr>
          </thead>

          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-neutral-800">
                {row.getVisibleCells().map((cell) => {
                  const w = cell.column.getSize();
                  return (
                    <td
                      key={cell.id}
                      className="px-3 py-2 border whitespace-nowrap overflow-hidden text-ellipsis"
                      style={{ width: w, maxWidth: w, minWidth: w }}
                      title={String(cell.getValue() ?? "")}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            ))}
            {table.getRowModel().rows.length === 0 && (
              <tr>
                <td
                  className="px-3 py-6 text-center text-gray-500"
                  colSpan={table.getVisibleFlatColumns().length}
                >
                  No decorations found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
